import { useTranslation } from 'react-i18next';
import { useTheme } from '../hooks/useTheme';
import { cn } from '../lib/utils';
import { THEME_OPTIONS } from '../lib/theme';

/**
 * Three-way theme picker (system / light / dark) rendered as a pill of icons
 * with the active one ringed. Reads and writes the ThemeProvider, so it can be
 * dropped anywhere inside it.
 */
export function ThemeSwitcher({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();

  // A single indicator that slides between the options, rather than a border
  // toggled on each button: that is what makes the change animate.
  const activeIndex = Math.max(
    THEME_OPTIONS.findIndex((option) => option.value === theme),
    0
  );

  return (
    <div
      role="radiogroup"
      aria-label={t('theme.label')}
      className={cn(
        'relative inline-flex items-center rounded-full bg-background ring-1 ring-inset ring-border',
        className
      )}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute left-0 top-0 size-8 rounded-full border border-foreground/40 transition-transform duration-300 ease-out"
        style={{ transform: `translateX(${activeIndex * 100}%)` }}
      />

      {THEME_OPTIONS.map(({ value, icon: Icon, labelKey }) => {
        const isActive = value === theme;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={t(labelKey)}
            title={t(labelKey)}
            data-active={isActive}
            onClick={() => setTheme(value)}
            className="relative flex size-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground data-[active=true]:text-foreground"
          >
            <Icon className="size-4" />
          </button>
        );
      })}
    </div>
  );
}
